import React, { useEffect } from 'react';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import { useQuery } from '@apollo/client';
import { cmsPage } from 'src/queries/__generated__/cmsPage';
import { NotFound } from 'src/components/NotFound/NotFound';
import { useDeps } from 'src/hooks/useDeps';
import { useSetResolved } from 'src/core/engine/router/useSetResolved';
import { PageTransitionSkeleton } from 'src/elements/Skeletons/Skeletons';

import getCmsPage from '../../queries/getCmsPage.graphql';
import { ErrorOrOfflineConnected } from '../Error/ErrorOrOffline';

import { CmsLayout, CmsPageInner } from './CmsPageInner';

type CmsPageProps = {
    id: number | null;
    page_layout?: CmsLayout;
};

export const CmsPageQuery: React.FC<CmsPageProps> = (props) => {
    const { setHead } = useHeadTags();
    // Resolve the route shell immediately so AsyncLoader does not stay blank
    // while the CMS query runs — the skeleton below covers the wait.
    useSetResolved();
    const { data, loading, error } = useQuery<cmsPage>(getCmsPage, {
        variables: { id: props.id },
        ssr: useDeps().env.SSR_GQL,
    });

    /**
     * IMPORTANT: keep this `useEffect` BEFORE any conditional return below.
     * Previously this hook lived after the loading/error/!data early-returns,
     * so on the first render (loading=true) only 2 hooks ran and on the
     * second render (loading=false) 3 hooks ran. React 19 enforces the
     * Rules of Hooks strictly and throws "Rendered more hooks than during
     * the previous render" — the error boundary then unmounts the page,
     * producing the "loads briefly then white-screen" symptom on every
     * CMS route (/deals, /payment-options, etc.).
     */
    const cmsPageData = data?.cmsPage;
    const title = cmsPageData?.title;
    const meta_title = cmsPageData?.meta_title;
    const meta_description = cmsPageData?.meta_description;

    useEffect(() => {
        if (!cmsPageData) return;
        const t = meta_title || title || '';
        const d = meta_description || '';
        setHead({
            title: `<title>${escapeAttr(t)}</title>`,
            meta: [
                `<meta name="description" content="${escapeAttr(d)}" />`,
                `<meta property="og:title" content="${escapeAttr(t)}" />`,
                `<meta property="og:description" content="${escapeAttr(d)}" />`,
            ].join(''),
        });
    }, [cmsPageData, meta_title, meta_description, title, setHead]);

    if (error) return <ErrorOrOfflineConnected />;
    if (loading) return <PageTransitionSkeleton />;
    if (!cmsPageData) {
        console.error('cmsPage data not found for id: %s', props.id);
        return <NotFound />;
    }
    const { content, content_heading, page_layout } = cmsPageData;

    return (
        <CmsPageInner
            title={title ?? null}
            content_heading={content_heading ?? null}
            content={content || `content missing for '${title}', id: '${props.id}'`}
            page_layout={props.page_layout || page_layout!}
        />
    );
};
